import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import AuthProvider from '@/context/AuthProvider';
import XeroProvider from '@/context/XeroProvider';
import HomeContextProvider from '@/context/HomeProvider';
import { routes } from '@/utils/routes';
import lazyWithRetry from '@/utils/lazyWithRetry';
import { isMobileView } from '@/utils/responsive';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';
import V3HomePage from './pages/Home';
import EmergeHome from './pages/Home/EmergeHome';
import EmergePayments from './pages/Payments';
import EmergeAccounts from './pages/Accounts';
import EmergeGeneralLedgerSettings from './pages/GeneralLedgerSettings';

// Lazy load pages
const LoginPage = lazyWithRetry(() => import('@/pages/Login'), 'login');
const MobileViewPage = lazyWithRetry(() => import('@/pages/common/MobilePage'), 'mobile_page');
const PageNotFound = lazyWithRetry(() => import('@/pages/common/PageNotFound'), 'page_not_found');
const PaymentPage = lazyWithRetry(() => import('@/pages/Home/Payment'), 'pay_a_person_or_company');
const TransferPage = lazyWithRetry(
  () => import('@/pages/Home/TransferMoney'),
  'transfer_to_person_or_company'
);
const NewBatchPaymentPage = lazyWithRetry(
  () => import('@/pages/Home/NewBatchPayment'),
  'new_batch_payment'
);
const PasskeySettingsPage = lazyWithRetry(
  () => import('@/pages/Home/PasskeySettings'),
  'passkey_settings'
);
const TeamMembersPage = lazyWithRetry(() => import('./pages/TeamMembers'), 'team_members');
/**
 * V3 App — New design implementation
 * Left sidebar navigation with main content area
 */
const V3App = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(isMobileView());

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.search) {
      navigate({ pathname: routes.base, search: url.search });
    }

    const handleUnhandledRejection = (err: PromiseRejectionEvent) => {
      console.error(err);
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, [navigate]);

  useLayoutEffect(() => {
    setIsMobile(isMobileView());
    const handleResize = () => setIsMobile(isMobileView());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div id="App">
      <AuthProvider>
        <XeroProvider>
          <HomeContextProvider>
            {isMobile ? (
              <div className="min-w-dvw min-h-dvh max-w-dvw max-h-dvh h-full w-full font-sans">
                <MobileViewPage />
              </div>
            ) : (
              <Routes>
                <Route index element={<LoginPage />} />
                <Route path={routes.home} element={<V3HomePage />}>
                  <Route index element={<EmergeHome />} />
                  {isFeatureEnabled(Feature.SELECTED_ACCOUNT) && (
                    <Route path={routes.selectedAccount} element={<EmergeAccounts />} />
                  )}
                  {/* Feature.MANAGE_PAYMENT gates the dashboard itself (scheduled payments and
                      batch payment monitoring together) - Feature.BATCH_PAYMENT separately gates
                      only whether batch groups appear within it, inside useManagePayment. */}
                  {isFeatureEnabled(Feature.MANAGE_PAYMENT) && (
                    <Route path={routes.managePayment} element={<EmergePayments />} />
                  )}
                  {isFeatureEnabled(Feature.PAYMENT) && (
                    <Route path={routes.payment} element={<PaymentPage />} />
                  )}
                  <Route path={routes.transferMoney} element={<TransferPage />} />
                  {isFeatureEnabled(Feature.BATCH_PAYMENT) && (
                    <Route path={routes.newBatchPayment} element={<NewBatchPaymentPage />} />
                  )}
                  <Route
                    path={routes.generalLedgerSettings}
                    element={<EmergeGeneralLedgerSettings />}
                  />
                  <Route path={routes.passkeySettings} element={<PasskeySettingsPage />} />
                  {isFeatureEnabled(Feature.TEAM_MEMBERS) && (
                    <Route path={routes.teamMembers} element={<TeamMembersPage />} />
                  )}
                  {/* TODO: Add V3-specific page routes here as features are migrated */}
                </Route>
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            )}
          </HomeContextProvider>
        </XeroProvider>
      </AuthProvider>
    </div>
  );
};

export default V3App;
